"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Modal variant type
export type ModalVariant = "default";

// Icon types that can be rendered in the toolbar
export type WindowControlIcon = "close" | "minimize" | "maximize" | "restore";

// SVG Icon Components
const WindowControlIcons = {
  close: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  minimize: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  maximize: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    </svg>
  ),
  restore: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  ),
};

// Button Pod Component - represents a single button in the toolbar
export interface ToolbarButtonProps {
  icon?: WindowControlIcon | React.ReactNode;
  label?: string;
  onClick?: () => void;
  variant?: "default" | "primary" | "danger";
  disabled?: boolean;
  className?: string;
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    { icon, label, onClick, variant = "default", disabled = false, className },
    ref,
  ) => {
    const variantStyles = {
      default: "hover:bg-accent",
      primary: "hover:bg-primary/20",
      danger: "hover:bg-destructive/20 text-destructive",
    };

    const renderIcon = () => {
      if (!icon) return null;

      if (typeof icon === "string" && icon in WindowControlIcons) {
        const IconComponent = WindowControlIcons[icon as WindowControlIcon];
        return <IconComponent className="h-3 w-3 text-foreground" />;
      }

      return icon;
    };

    return (
      <button
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "duration-250 flex h-6 w-6 touch-none items-center justify-center rounded transition-all",
          "disabled:cursor-not-allowed disabled:opacity-50",
          variantStyles[variant],
          className,
        )}
        aria-label={label}
      >
        {renderIcon()}
        {label && <span className="ml-1 text-xs">{label}</span>}
      </button>
    );
  },
);
ToolbarButton.displayName = "ToolbarButton";

// Toolbar Component - manages button layout and responsive behavior
interface ToolbarProps {
  buttons?: ToolbarButtonProps[];
  align?: "left" | "center" | "right";
  collapseAt?: number; // Number of buttons before collapsing to menu
  className?: string;
}

const Toolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(
  (
    {
      buttons = [],
      align = "left",
      collapseAt = 4,
      className,
    },
    ref,
  ) => {
    const [isCollapsed, setIsCollapsed] = React.useState(false);

    React.useEffect(() => {
      const handleResize = () => {
        setIsCollapsed(window.innerWidth < 640 && buttons.length > collapseAt);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [buttons.length, collapseAt]);

    const alignStyles = {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end",
    };

    if (isCollapsed) {
      return (
        <div
          ref={ref}
          className={cn("flex items-center", alignStyles[align], className)}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-accent">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-2.5 w-2.5"
                >
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {buttons.map((button, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  className="cursor-pointer"
                >
                  {button.label || `Action ${index + 1}`}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-0", alignStyles[align], className)}
      >
        {buttons.map((button, index) => (
          <ToolbarButton key={index} {...button} />
        ))}
      </div>
    );
  },
);
Toolbar.displayName = "Toolbar";

// Main USLandModal Component
interface USLandModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  toolbarButtons?: ToolbarButtonProps[];
  toolbarButtonsLeft?: ToolbarButtonProps[];
  toolbarButtonsRight?: ToolbarButtonProps[];
  toolbarAlign?: "left" | "center" | "right";
  showDefaultClose?: boolean;
  variant?: ModalVariant;
  className?: string;
  contentClassName?: string;
  titleBarClassName?: string;
  zIndex?: string | number;
}

const USLandModal = React.forwardRef<HTMLDivElement, USLandModalProps>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      children,
      toolbarButtons = [],
      toolbarButtonsLeft,
      toolbarButtonsRight,
      toolbarAlign = "left",
      showDefaultClose = true,
      variant = "default",
      className,
      contentClassName,
      titleBarClassName,
      zIndex = "var(--z-modal-backdrop)",
    },
    ref,
  ) => {
    // Cleanup body scroll lock on unmount to prevent soft lock
    React.useEffect(() => {
      return () => {
        document.body.style.pointerEvents = "auto";
      };
    }, []);

    const defaultCloseButton: ToolbarButtonProps = {
      icon: "close",
      label: "Close",
      onClick: () => onOpenChange?.(false),
      variant: "danger",
    };

    // Support both old API (toolbarButtons) and new API (split left/right)
    let leftButtons: ToolbarButtonProps[] = [];
    let rightButtons: ToolbarButtonProps[] = [];

    if (toolbarButtonsLeft || toolbarButtonsRight) {
      // New API - split buttons
      leftButtons = showDefaultClose
        ? [defaultCloseButton, ...(toolbarButtonsLeft || [])]
        : toolbarButtonsLeft || [];
      rightButtons = toolbarButtonsRight || [];
    } else {
      // Old API - all buttons go to one side based on align
      const allButtons = showDefaultClose
        ? [defaultCloseButton, ...toolbarButtons]
        : toolbarButtons;
      if (toolbarAlign === "left") {
        leftButtons = allButtons;
      } else {
        rightButtons = allButtons;
      }
    }

    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          {/* Overlay */}
          <DialogPrimitive.Overlay
            style={{ zIndex: zIndex }}
            className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />

          {/* Content */}
          <DialogPrimitive.Content
            ref={ref}
            style={{ zIndex: "var(--z-modal)" }}
            className={cn(
              "fixed left-[50%] top-[50%] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] p-6 shadow-2xl",

              // Animation
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              // Base styles
              "flex flex-col overflow-hidden rounded-lg",

              "bg-background backdrop-blur-xl",

              className,
            )}
          >
            {/* Title Bar / Toolbar */}
            <div
              className={cn(
                "flex items-center justify-between gap-2 border-b border-border/50 bg-transparent px-3 py-1.5 backdrop-blur-sm",
                titleBarClassName,
              )}
            >
              {/* Left section - control buttons */}
              <div className="flex items-center">
                <Toolbar
                  buttons={leftButtons}
                  align="left"
                />
              </div>

              {/* Center section - title */}
              {title && (
                <DialogPrimitive.Title
                  className="flex-1 truncate px-3 text-center text-base font-semibold text-foreground"
                >
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description
                  className="flex-1 truncate px-3 text-center text-base font-semibold text-foreground"
                >
                  {description}
                </DialogPrimitive.Description>
              )}

              {/* Right section - action buttons */}
              <div className="flex items-center">
                <Toolbar
                  buttons={rightButtons}
                  align="right"
                />
              </div>
            </div>

            {/* Modal Content */}
            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto",
                !contentClassName?.includes("p-") && "p-6",
                contentClassName,
              )}
            >
              <div className="mx-auto min-h-full max-w-md">{children}</div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  },
);
USLandModal.displayName = "USLandModal";

// Trigger component for convenience
const USLandModalTrigger = DialogPrimitive.Trigger;

export { USLandModal, USLandModalTrigger, ToolbarButton, Toolbar };
export type { USLandModalProps };
